import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
} from '@zt/ui';
import type { Tone } from '@zt/ui';
import {
  AGE_BANDS,
  areasFor,
  basicsFor,
  emptyState,
  NEUTERED,
  recommend,
  REPRODUCTIVE,
  textOf,
  visibleQuestions,
  WEIGHT_BANDS,
  type Localized,
  type NeedsFinderDefinition,
  type NfCondition,
  type NfProduct,
  type NfState,
} from '@zt/content/needs-finder';
import { SPECIES, type Species } from '@zt/content';
import { ProductMultiPicker } from '../components/ProductMultiPicker';
import { api, type ApiError } from '../lib/api';
import type { HelpKey } from '@zt/help';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { Category, NeedsFinderReport, NeedsFinderSet, NeedsFinderView, Promotion } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';
import { idFrom } from '../lib/slug-id';

const N = 'admin.needsFinder';
type Def = NeedsFinderDefinition;
type Area = Def['areas'][number];
type Question = Def['questions'][number];
type Rule = Def['rules'][number];
type Exclusion = Def['exclusions'][number];
type Option = Question['options'][number];
type TabKey = 'areas' | 'questions' | 'rules' | 'exclusions' | 'texts' | 'preview' | 'report';
const TABS: TabKey[] = ['areas', 'questions', 'rules', 'exclusions', 'texts', 'preview', 'report'];
const LOCALES = ['en', 'de', 'ar'] as const;

const reviewTone: Record<NeedsFinderSet['reviewStatus'], Tone> = {
  not_submitted: 'neutral',
  pending: 'amber',
  approved: 'moss',
  rejected: 'rose',
};

const move = <T,>(xs: T[], i: number, d: -1 | 1): T[] => {
  const j = i + d;
  if (j < 0 || j >= xs.length) return xs;
  const out = [...xs];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
};

/**
 * İçerik → İhtiyaç bulucu (F7-39, ADR-147): Evcil Dostumun İhtiyacı soru setinin düzenleyicisi. Destek alanları,
 * sorular (dallanma koşullarıyla), öneri kuralları, hariç tutmalar ve metinler; panelde aynı kural motoruyla
 * önizleme; incelemeye gönder → Veteriner Uzman İnceleyici onayı → yayına al; kimliksiz toplam rapor.
 */
export function NeedsFinderPage() {
  const t = useT();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const q = useQuery({
    queryKey: ['needs-finder', storeId],
    queryFn: () => api.get<NeedsFinderView>(`/v1/admin/needs-finder?storeId=${storeId}`),
    enabled: !!storeId,
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame helpKey="page.needs_finder" wide>
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <div className="space-y-6">
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          {!q.data ? (
            <Spinner label={t('common.loading')} />
          ) : !q.data.set ? (
            <StartPanel storeId={storeId} />
          ) : (
            <Workspace key={`${storeId}:${q.data.set.version}`} set={q.data.set} storeId={storeId} />
          )}
        </div>
      )}
    </PageFrame>
  );
}

/** Taslak yokken: ürün verisinden ilk taslak veya boş taslak. */
function StartPanel({ storeId }: { storeId: string }) {
  const t = useT();
  const { can } = usePermissions();
  const generate = useApiMutation(
    () => api.post<NeedsFinderView>('/v1/admin/needs-finder/generate', { storeId }),
    { invalidate: [['needs-finder', storeId]], success: t(`${N}.generated`) },
  );
  const empty = useApiMutation(
    () =>
      api.put<NeedsFinderView>('/v1/admin/needs-finder/draft', {
        storeId,
        definition: { areas: [], questions: [], rules: [], exclusions: [] },
      }),
    { invalidate: [['needs-finder', storeId]] },
  );
  return (
    <Section
      title={t(`${N}.startTitle`)}
      description={t(`${N}.startLead`)}
      action={<HelpTip k="needs_finder.generate" />}
    >
      {can('content.write') ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" disabled={generate.isPending} onClick={() => generate.mutate(undefined)}>
            {t(`${N}.generate`)}
          </Button>
          <Button disabled={empty.isPending} onClick={() => empty.mutate(undefined)}>
            {t(`${N}.startEmpty`)}
          </Button>
          <HelpTip k="needs_finder.start_empty" />
        </div>
      ) : (
        <p className="text-sm text-ink-2">{t(`${N}.noDraft`)}</p>
      )}
    </Section>
  );
}

function Workspace({ set, storeId }: { set: NeedsFinderSet; storeId: string }) {
  const t = useT();
  const { can } = usePermissions();
  const canWrite = can('content.write');
  const [draft, setDraft] = useState<Def>(set.draft);
  const [tab, setTab] = useState<TabKey>('areas');
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(set.draft), [draft, set.draft]);
  const save = useApiMutation(
    () =>
      api.put<NeedsFinderView>('/v1/admin/needs-finder/draft', {
        storeId,
        definition: draft,
        version: set.version,
      }),
    { invalidate: [['needs-finder', storeId]], success: t(`${N}.saved`), silentFieldErrors: true },
  );
  // Kaydedilmemiş değişiklik varken sayfadan çıkışta tarayıcı uyarır.
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);
  const titles = useMemo(() => new Map(set.products.map((p) => [p.id, p.title])), [set.products]);
  return (
    <div className="space-y-6">
      <StatusPanel set={set} storeId={storeId} dirty={dirty} />
      <ReviewPanel set={set} storeId={storeId} dirty={dirty} />
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-line bg-paper py-2">
        <Button
          variant="primary"
          disabled={!canWrite || !dirty || save.isPending}
          onClick={() => save.mutate(undefined)}
        >
          {t(`${N}.save`)}
        </Button>
        <HelpTip k="needs_finder.save" />
        {dirty && <span className="text-sm text-amber">{t(`${N}.unsaved`)}</span>}
        {dirty && (
          <Button variant="ghost" onClick={() => setDraft(set.draft)}>
            {t(`${N}.discard`)}
          </Button>
        )}
      </div>
      <SaveErrors error={save.error} draft={draft} />
      <Tabs value={tab} onChange={setTab} />
      <div role="tabpanel" id={`nf-panel-${tab}`} aria-labelledby={`nf-tab-${tab}`}>
        {tab === 'areas' && <AreasTab draft={draft} setDraft={setDraft} canWrite={canWrite} />}
        {tab === 'questions' && <QuestionsTab draft={draft} setDraft={setDraft} canWrite={canWrite} />}
        {tab === 'rules' && (
          <RulesTab draft={draft} setDraft={setDraft} canWrite={canWrite} titles={titles} />
        )}
        {tab === 'exclusions' && (
          <ExclusionsTab draft={draft} setDraft={setDraft} canWrite={canWrite} titles={titles} />
        )}
        {tab === 'texts' && (
          <TextsTab draft={draft} setDraft={setDraft} canWrite={canWrite} storeId={storeId} />
        )}
        {tab === 'preview' && <PreviewTab draft={draft} set={set} titles={titles} />}
        {tab === 'report' && <ReportTab storeId={storeId} />}
      </div>
    </div>
  );
}

function Tabs({ value, onChange }: { value: TabKey; onChange: (k: TabKey) => void }) {
  const t = useT();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const onKey = (e: KeyboardEvent, i: number) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!d && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const rtl = document.documentElement.dir === 'rtl';
    const j =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? TABS.length - 1
          : (i + (rtl ? -d : d) + TABS.length) % TABS.length;
    onChange(TABS[j]!);
    refs.current[j]?.focus();
  };
  return (
    <div role="tablist" aria-label={t(`${N}.editor`)} className="flex flex-wrap gap-1 border-b border-line">
      {TABS.map((k, i) => (
        <button
          key={k}
          ref={(el) => {
            refs.current[i] = el;
          }}
          id={`nf-tab-${k}`}
          role="tab"
          type="button"
          aria-selected={value === k}
          aria-controls={`nf-panel-${k}`}
          tabIndex={value === k ? 0 : -1}
          onClick={() => onChange(k)}
          onKeyDown={(e) => onKey(e, i)}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
            value === k ? 'border-pine text-ink' : 'border-transparent text-ink-2 hover:text-ink'
          }`}
        >
          {t(`${N}.tabs.${k}`)}
        </button>
      ))}
    </div>
  );
}

/** Sunucu doğrulama hataları: tanımdaki yerine göre okunur biçimde. */
function SaveErrors({ error, draft }: { error: ApiError | null; draft: Def }) {
  const t = useT();
  if (!error?.errors.length) return null;
  const where = (field: string) => {
    const [part, idx, ...rest] = field.split('.');
    const i = Number(idx);
    const label =
      part === 'areas'
        ? textOf(draft.areas[i]?.label, 'tr')
        : part === 'questions'
          ? textOf(draft.questions[i]?.text, 'tr')
          : part === 'rules' || part === 'exclusions'
            ? `#${i + 1}`
            : '';
    return [
      t(`${N}.tabs.${part === 'bundlePromotionId' ? 'texts' : part}`),
      label,
      rest.length ? rest.join('.') : '',
    ]
      .filter(Boolean)
      .join(' → ');
  };
  return (
    <div role="alert" className="rounded-md border border-rose/30 bg-rose-tint p-3 text-sm">
      <p className="font-medium">{t(`${N}.saveErrors`)}</p>
      <ul className="mt-1 list-disc space-y-0.5 ps-5">
        {error.errors.map((e, i) => (
          <li key={i}>
            <span className="text-ink-2">{where(e.field)}:</span> {e.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Onay penceresi (geri alınması zor işlemler için). */
function Confirm({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
}) {
  const t = useT();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-ink-2">{description}</p>
    </Dialog>
  );
}

// ------------------------------------------------------------------------------------------- Durum ve yayın

function StatusPanel({ set, storeId, dirty }: { set: NeedsFinderSet; storeId: string; dirty: boolean }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [confirm, setConfirm] = useState<null | 'regenerate' | 'unpublish'>(null);
  const inv = { invalidate: [['needs-finder', storeId]] };
  const post = (path: string, body: Record<string, unknown> = {}) =>
    api.post<NeedsFinderView>(`/v1/admin/needs-finder/${path}`, { storeId, version: set.version, ...body });
  const submit = useApiMutation(() => post('submit'), { ...inv, success: t(`${N}.submitted`) });
  const publish = useApiMutation(() => post('publish'), { ...inv, success: t(`${N}.publishedToast`) });
  const unpublish = useApiMutation(() => post('unpublish'), { ...inv, success: t(`${N}.unpublishedToast`) });
  const regenerate = useApiMutation(() => post('generate', { replace: true }), {
    ...inv,
    success: t(`${N}.generated`),
  });
  const settings = useApiMutation(
    (on: boolean) =>
      api.put<NeedsFinderView>('/v1/admin/needs-finder/settings', {
        storeId,
        version: set.version,
        petProfilesEnabled: on,
      }),
    inv,
  );
  const blocking = set.problems.filter((p) => p.blocking);
  const warnings = set.problems.filter((p) => !p.blocking);
  const approved = set.reviewStatus === 'approved' && set.reviewCurrent;
  const busy = submit.isPending || publish.isPending || unpublish.isPending || regenerate.isPending;
  return (
    <Section
      title={t(`${N}.statusTitle`)}
      description={t(`${N}.statusLead`)}
      action={<HelpTip k="needs_finder.status" />}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={reviewTone[set.reviewStatus]}>{t(`${N}.review.${set.reviewStatus}`)}</Badge>
          <Badge tone={set.published ? 'moss' : 'neutral'}>
            {set.published ? t(`${N}.live`) : t(`${N}.notLive`)}
          </Badge>
          {set.published && !set.publishedCurrent && <Badge tone="amber">{t(`${N}.liveDiffers`)}</Badge>}
          {set.publishedAt && (
            <span className="text-sm text-ink-2">
              {t(`${N}.publishedAt`, { date: formatDate(set.publishedAt) })}
            </span>
          )}
        </div>
        {set.generatedAt && <p className="text-sm text-ink-2">{t(`${N}.generatedNote`)}</p>}
        {set.reviewNote && set.reviewStatus === 'rejected' && (
          <p className="text-sm">{t(`${N}.reviewNote`, { note: set.reviewNote })}</p>
        )}
        {(blocking.length > 0 || warnings.length > 0) && (
          <div className="space-y-2" aria-label={t(`${N}.problemsTitle`)}>
            <div className="flex items-center gap-1 text-sm font-medium">
              {t(`${N}.problemsTitle`)} <HelpTip k="needs_finder.problems" />
            </div>
            <ul className="space-y-1 text-sm">
              {[...blocking, ...warnings].map((p, i) => (
                <li key={i} className={p.blocking ? 'text-rose' : 'text-amber'}>
                  {t(`${N}.problems.${p.code}`, {
                    product: p.productId ? (set.products.find((x) => x.id === p.productId)?.title ?? '') : '',
                    ref: p.ref ?? '',
                  })}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {can('content.write') && (
            <>
              <Button
                disabled={
                  busy ||
                  dirty ||
                  blocking.length > 0 ||
                  (set.reviewStatus === 'pending' && set.reviewCurrent)
                }
                onClick={() => submit.mutate(undefined)}
              >
                {t(`${N}.submit`)}
              </Button>
              <HelpTip k="needs_finder.submit" />
            </>
          )}
          {can('content.publish') && (
            <>
              <Button
                variant="primary"
                disabled={busy || dirty || !approved || (set.published && set.publishedCurrent)}
                onClick={() => publish.mutate(undefined)}
              >
                {t(`${N}.publish`)}
              </Button>
              <HelpTip k="needs_finder.publish" />
              {set.published && (
                <>
                  <Button variant="ghost" disabled={busy} onClick={() => setConfirm('unpublish')}>
                    {t(`${N}.unpublish`)}
                  </Button>
                  <HelpTip k="needs_finder.unpublish" />
                </>
              )}
            </>
          )}
          {can('content.write') && (
            <>
              <Button variant="ghost" disabled={busy} onClick={() => setConfirm('regenerate')}>
                {t(`${N}.regenerate`)}
              </Button>
              <HelpTip k="needs_finder.regenerate" />
            </>
          )}
        </div>
        {dirty && <p className="text-sm text-ink-2">{t(`${N}.saveFirst`)}</p>}
        <div className="flex items-start gap-3 rounded-md border border-line p-3">
          <Switch
            checked={set.petProfilesEnabled}
            disabled={!can('content.publish') || settings.isPending}
            onCheckedChange={(v) => settings.mutate(v)}
            label={t(`${N}.petProfiles`)}
          />
          <div className="space-y-0.5 text-sm">
            <div className="flex items-center gap-1 font-medium">
              {t(`${N}.petProfiles`)} <HelpTip k="needs_finder.pet_profiles" />
            </div>
            <p className="text-ink-2">{t(`${N}.petProfilesLead`)}</p>
          </div>
        </div>
      </div>
      <Confirm
        open={confirm === 'regenerate'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={t(`${N}.regenerateTitle`)}
        description={t(`${N}.regenerateLead`)}
        confirmLabel={t(`${N}.regenerate`)}
        onConfirm={() => {
          setConfirm(null);
          regenerate.mutate(undefined);
        }}
      />
      <Confirm
        open={confirm === 'unpublish'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={t(`${N}.unpublishTitle`)}
        description={t(`${N}.unpublishLead`)}
        confirmLabel={t(`${N}.unpublish`)}
        onConfirm={() => {
          setConfirm(null);
          unpublish.mutate(undefined);
        }}
      />
    </Section>
  );
}

/** Uzman incelemesi: önerilecek ürünlerin tam listesi, sağlık beyanı bulguları, onay / gerekçeli ret. */
function ReviewPanel({ set, storeId, dirty }: { set: NeedsFinderSet; storeId: string; dirty: boolean }) {
  const t = useT();
  const { can } = usePermissions();
  const [note, setNote] = useState('');
  const decide = useApiMutation(
    (decision: 'approved' | 'rejected') =>
      api.post<NeedsFinderView>('/v1/admin/needs-finder/review', {
        storeId,
        version: set.version,
        decision,
        contentHash: set.draftHash,
        note: note.trim() || undefined,
      }),
    { invalidate: [['needs-finder', storeId]], success: t(`${N}.decided`) },
  );
  const pending = set.reviewStatus === 'pending' && set.reviewCurrent;
  const canReview = can('content.expert_review');
  if (!pending && !set.findings.length) return null;
  const rulesOf = (pid: string) =>
    set.draft.rules.filter((r) => r.productId === pid || r.alternatives.includes(pid));
  return (
    <Section
      title={t(`${N}.reviewTitle`)}
      description={t(`${N}.reviewLead`)}
      action={<HelpTip k="needs_finder.review" />}
    >
      <div className="space-y-4">
        {set.findings.length > 0 && (
          <div className="rounded-md border border-amber/30 bg-amber-tint p-3 text-sm">
            <p className="flex items-center gap-1 font-medium">
              {t(`${N}.findings`, { count: set.findings.length })} <HelpTip k="needs_finder.findings" />
            </p>
            <ul className="mt-1 list-disc space-y-0.5 ps-5">
              {set.findings.map((f, i) => (
                <li key={i}>
                  <strong>{f.match}</strong> — {t(`healthClaims.${f.code}`)}: “{f.excerpt}”
                </li>
              ))}
            </ul>
          </div>
        )}
        {pending && (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" aria-label={t(`${N}.productsTitle`)}>
              <caption className="flex items-center gap-1 px-3 pt-2 text-start font-medium">
                {t(`${N}.productsTitle`)} <HelpTip k="needs_finder.products" />
              </caption>
              <thead className="text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t(`${N}.colProduct`)}</th>
                  <th className="px-3 py-2 text-start font-medium">{t(`${N}.colSpecies`)}</th>
                  <th className="px-3 py-2 text-start font-medium">{t(`${N}.colReasons`)}</th>
                  <th className="px-3 py-2 text-start font-medium">{t(`${N}.colState`)}</th>
                </tr>
              </thead>
              <tbody>
                {set.products.map((p) => (
                  <tr key={p.id} className="border-t border-line align-top">
                    <td className="px-3 py-2">{p.title}</td>
                    <td className="px-3 py-2">
                      {p.species.map((s) => t(`admin.products.speciesNames.${s}`)).join(', ') || '—'}
                    </td>
                    <td className="px-3 py-2">
                      <ul className="space-y-0.5">
                        {rulesOf(p.id).map((r) => (
                          <li key={r.id}>{textOf(r.reason, 'tr')}</li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-3 py-2">
                      {p.visible ? t(`${N}.visible`) : t(`${N}.hidden`)}
                      {!p.hasUsage && <span className="block text-amber">{t(`${N}.noUsage`)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pending && canReview && (
          <div className="grid max-w-xl gap-2">
            {dirty && <p className="text-sm text-ink-2">{t(`${N}.saveFirst`)}</p>}
            <Field
              label={t(`${N}.reviewNoteLabel`)}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="needs_finder.review_note" />}
            >
              {(p) => (
                <Textarea {...p} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
              )}
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                disabled={decide.isPending || dirty}
                onClick={() => decide.mutate('approved')}
              >
                {t(`${N}.approve`)}
              </Button>
              <Button
                variant="danger"
                disabled={decide.isPending || dirty || !note.trim()}
                onClick={() => decide.mutate('rejected')}
              >
                {t(`${N}.reject`)}
              </Button>
              <HelpTip k="needs_finder.review" />
            </div>
          </div>
        )}
        {pending && !canReview && <p className="text-sm text-ink-2">{t(`${N}.waitingExpert`)}</p>}
      </div>
    </Section>
  );
}

// ------------------------------------------------------------------------------------------- Ortak düzenleyiciler

function LocalizedField({
  label,
  help,
  value,
  onChange,
  multiline,
  optional,
  disabled,
}: {
  label: string;
  help: HelpKey;
  value: Localized | undefined;
  onChange: (v: Localized | undefined) => void;
  multiline?: boolean;
  optional?: boolean;
  disabled?: boolean;
}) {
  const t = useT();
  const v = value ?? { tr: '' };
  const set = (loc: keyof Localized, text: string) => {
    const next: Localized = { ...v, [loc]: text };
    if (loc !== 'tr' && !text) delete next[loc];
    onChange(optional && !next.tr && !next.en && !next.de && !next.ar ? undefined : next);
  };
  const Control = multiline ? Textarea : Input;
  return (
    <div className="space-y-2">
      <Field
        label={label}
        help={<HelpTip k={help} />}
        optionalLabel={optional ? t('common.optional') : undefined}
      >
        {(p) => (
          <Control
            {...p}
            value={v.tr}
            maxLength={600}
            disabled={disabled}
            onChange={(e) => set('tr', e.target.value)}
          />
        )}
      </Field>
      {/* Yardım düğmesi özetin dışında: <summary> içinde başka odaklanabilir öğe olmamalı (axe nested-interactive). */}
      <details className="text-sm">
        <summary className="cursor-pointer text-ink-2">{t(`${N}.otherLanguages`)}</summary>
        <div className="mt-1">
          <HelpTip k="needs_finder.translations" />
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          {LOCALES.map((loc) => (
            <Field key={loc} label={t(`${N}.lang.${loc}`)} optionalLabel={t('common.optional')}>
              {(p) => (
                <Control
                  {...p}
                  dir={loc === 'ar' ? 'rtl' : undefined}
                  value={v[loc] ?? ''}
                  maxLength={600}
                  disabled={disabled}
                  onChange={(e) => set(loc, e.target.value)}
                />
              )}
            </Field>
          ))}
        </div>
      </details>
    </div>
  );
}

function CheckGroup<T extends string>({
  legend,
  values,
  selected,
  labelOf,
  onChange,
  disabled,
  help,
}: {
  legend: string;
  values: readonly T[];
  selected: readonly T[] | undefined;
  labelOf: (v: T) => string;
  onChange: (v: T[] | undefined) => void;
  disabled?: boolean;
  help?: HelpKey;
}) {
  const sel = selected ?? [];
  return (
    <fieldset className="space-y-1">
      <legend className="flex items-center gap-1 text-sm font-medium">
        {legend} {help && <HelpTip k={help} />}
      </legend>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {values.map((v) => (
          <label key={v} className="inline-flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              className="accent-[var(--zt-pine)]"
              disabled={disabled}
              checked={sel.includes(v)}
              onChange={(e) => {
                const next = e.target.checked ? [...sel, v] : sel.filter((x) => x !== v);
                onChange(next.length ? next : undefined);
              }}
            />
            {labelOf(v)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Koşul: verilen her bölüm tutmalı (VE); bir bölümdeki seçeneklerden biri yeterli (VEYA). Boş = her zaman. */
function ConditionEditor({
  value,
  onChange,
  draft,
  excludeQuestion,
}: {
  value: NfCondition | undefined;
  onChange: (v: NfCondition) => void;
  draft: Def;
  /** Sorunun kendi koşulunda kendi cevabı seçilemez. */
  excludeQuestion?: string;
}) {
  const t = useT();
  const c = value ?? {};
  const patch = (p: Partial<NfCondition>) => {
    const next = { ...c, ...p } as Record<string, unknown>;
    for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
    onChange(next as NfCondition);
  };
  const questions = draft.questions.filter((q) => q.id !== excludeQuestion);
  const answerQ = questions.find((q) => q.id === c.answer?.questionId);
  return (
    <fieldset className="space-y-3 rounded-md border border-line p-3">
      <legend className="flex items-center gap-1 px-1 text-sm font-medium">
        {t(`${N}.condition`)} <HelpTip k="needs_finder.condition" />
      </legend>
      <p className="text-[0.8125rem] text-ink-3">{t(`${N}.conditionLead`)}</p>
      <CheckGroup
        legend={t(`${N}.cond.species`)}
        values={SPECIES}
        selected={c.species}
        labelOf={(s) => t(`admin.products.speciesNames.${s}`)}
        onChange={(species) => patch({ species })}
      />
      <CheckGroup
        legend={t(`${N}.cond.ages`)}
        values={AGE_BANDS}
        selected={c.ages}
        labelOf={(v) => t(`storefront.needsFinder.ages.${v}`)}
        onChange={(ages) => patch({ ages })}
      />
      <CheckGroup
        legend={t(`${N}.cond.weights`)}
        values={WEIGHT_BANDS}
        selected={c.weights}
        labelOf={(v) => t(`storefront.needsFinder.weights.${v}`)}
        onChange={(weights) => patch({ weights })}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t(`${N}.cond.neutered`)}>
          {(p) => (
            <Select
              {...p}
              value={c.neutered ?? ''}
              onChange={(e) => patch({ neutered: (e.target.value || undefined) as NfCondition['neutered'] })}
            >
              <option value="">{t(`${N}.cond.any`)}</option>
              <option value="yes">{t('storefront.needsFinder.neuteredOptions.yes')}</option>
              <option value="no">{t('storefront.needsFinder.neuteredOptions.no')}</option>
            </Select>
          )}
        </Field>
        <CheckGroup
          legend={t(`${N}.cond.reproductive`)}
          values={['pregnant', 'lactating'] as const}
          selected={c.reproductive}
          labelOf={(v) => t(`storefront.needsFinder.reproductiveOptions.${v}`)}
          onChange={(reproductive) => patch({ reproductive })}
        />
      </div>
      {draft.areas.length > 0 && (
        <CheckGroup
          legend={t(`${N}.cond.areas`)}
          values={draft.areas.map((a) => a.id)}
          selected={c.areas}
          labelOf={(id) => textOf(draft.areas.find((a) => a.id === id)?.label, 'tr')}
          onChange={(areas) => patch({ areas })}
        />
      )}
      {questions.length > 0 && (
        <div className="grid gap-2">
          <Field label={t(`${N}.cond.answer`)}>
            {(p) => (
              <Select
                {...p}
                value={c.answer?.questionId ?? ''}
                onChange={(e) =>
                  patch({
                    answer: e.target.value ? { questionId: e.target.value, optionIds: [] } : undefined,
                  })
                }
              >
                <option value="">{t(`${N}.cond.noAnswer`)}</option>
                {questions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {textOf(q.text, 'tr')}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {answerQ && (
            <CheckGroup
              legend={t(`${N}.cond.answerOptions`)}
              values={answerQ.options.map((o) => o.id)}
              selected={c.answer?.optionIds}
              labelOf={(id) => textOf(answerQ.options.find((o) => o.id === id)?.label, 'tr')}
              onChange={(ids) => patch({ answer: { questionId: answerQ.id, optionIds: ids ?? [] } })}
            />
          )}
        </div>
      )}
    </fieldset>
  );
}

/** Koşulun kısa özeti (listelerde). */
function useConditionText(draft: Def) {
  const t = useT();
  return (c: NfCondition | undefined): string => {
    if (!c) return t(`${N}.always`);
    const parts: string[] = [];
    if (c.species?.length) parts.push(c.species.map((s) => t(`admin.products.speciesNames.${s}`)).join('/'));
    if (c.ages?.length) parts.push(c.ages.map((v) => t(`storefront.needsFinder.ages.${v}`)).join('/'));
    if (c.weights?.length)
      parts.push(c.weights.map((v) => t(`storefront.needsFinder.weights.${v}`)).join('/'));
    if (c.neutered)
      parts.push(`${t(`${N}.cond.neutered`)}: ${t(`storefront.needsFinder.neuteredOptions.${c.neutered}`)}`);
    if (c.reproductive?.length)
      parts.push(c.reproductive.map((v) => t(`storefront.needsFinder.reproductiveOptions.${v}`)).join('/'));
    if (c.areas?.length)
      parts.push(c.areas.map((a) => textOf(draft.areas.find((x) => x.id === a)?.label, 'tr') || a).join('/'));
    if (c.answer) {
      const q = draft.questions.find((x) => x.id === c.answer!.questionId);
      parts.push(
        `${textOf(q?.text, 'tr')} = ${c.answer.optionIds
          .map((o) => textOf(q?.options.find((x) => x.id === o)?.label, 'tr') || o)
          .join('/')}`,
      );
    }
    return parts.length ? parts.join('; ') : t(`${N}.always`);
  };
}

function RowActions({
  index,
  count,
  onMove,
  onEdit,
  onDelete,
  canWrite,
  label,
}: {
  index: number;
  count: number;
  onMove: (d: -1 | 1) => void;
  onEdit: () => void;
  onDelete: () => void;
  canWrite: boolean;
  label: string;
}) {
  const t = useT();
  return (
    <div className="flex shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        aria-label={t(`${N}.moveUp`, { name: label })}
        disabled={!canWrite || index === 0}
        onClick={() => onMove(-1)}
      >
        <ArrowUp size={16} aria-hidden />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-label={t(`${N}.moveDown`, { name: label })}
        disabled={!canWrite || index === count - 1}
        onClick={() => onMove(1)}
      >
        <ArrowDown size={16} aria-hidden />
      </Button>
      <Button variant="ghost" size="sm" aria-label={t(`${N}.edit`, { name: label })} onClick={onEdit}>
        <Pencil size={16} aria-hidden />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-label={t(`${N}.delete`, { name: label })}
        disabled={!canWrite}
        onClick={onDelete}
      >
        <Trash2 size={16} aria-hidden />
      </Button>
      <HelpTip k="needs_finder.row_actions" />
    </div>
  );
}

interface TabProps {
  draft: Def;
  setDraft: (d: Def) => void;
  canWrite: boolean;
}

/** Liste + ekle/düzenle penceresi kalıbı. */
function ListTab<T extends { id: string }>({
  title,
  lead,
  help,
  items,
  setItems,
  canWrite,
  rowTitle,
  rowMeta,
  newItem,
  editor,
  addLabel,
  emptyLabel,
  dialogTitle,
}: {
  title: string;
  lead: string;
  help: HelpKey;
  items: T[];
  setItems: (xs: T[]) => void;
  canWrite: boolean;
  rowTitle: (x: T) => string;
  rowMeta: (x: T) => ReactNode;
  newItem: () => T;
  editor: (x: T, set: (x: T) => void) => ReactNode;
  addLabel: string;
  emptyLabel: string;
  dialogTitle: string;
}) {
  const t = useT();
  const [editing, setEditing] = useState<{ index: number; value: T } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  return (
    <Section title={title} description={lead} action={<HelpTip k={help} />}>
      <div className="space-y-3">
        {items.length ? (
          <ol className="divide-y divide-line rounded-md border border-line">
            {items.map((x, i) => (
              <li key={x.id} className="flex items-start justify-between gap-3 p-3">
                <div className="min-w-0 space-y-0.5">
                  <p className="font-medium">{rowTitle(x) || <span className="text-ink-3">{x.id}</span>}</p>
                  <div className="text-[0.8125rem] text-ink-2">{rowMeta(x)}</div>
                </div>
                <RowActions
                  index={i}
                  count={items.length}
                  label={rowTitle(x) || x.id}
                  canWrite={canWrite}
                  onMove={(d) => setItems(move(items, i, d))}
                  onEdit={() => setEditing({ index: i, value: structuredClone(x) })}
                  onDelete={() => setConfirmDelete(i)}
                />
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-ink-2">{emptyLabel}</p>
        )}
        {canWrite && (
          <Button onClick={() => setEditing({ index: -1, value: newItem() })}>
            <Plus size={16} aria-hidden /> {addLabel}
          </Button>
        )}
      </div>
      <Dialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={dialogTitle}
        closeLabel={t('common.close')}
        variant="sheet"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={!canWrite}
              onClick={() => {
                if (!editing) return;
                const next = [...items];
                if (editing.index < 0) next.push(editing.value);
                else next[editing.index] = editing.value;
                setItems(next);
                setEditing(null);
              }}
            >
              {t(`${N}.apply`)}
            </Button>
          </div>
        }
      >
        {editing && editor(editing.value, (value) => setEditing({ ...editing, value }))}
      </Dialog>
      <Confirm
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={t(`${N}.deleteTitle`)}
        description={t(`${N}.deleteLead`)}
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (confirmDelete !== null) setItems(items.filter((_, i) => i !== confirmDelete));
          setConfirmDelete(null);
        }}
      />
    </Section>
  );
}

// ------------------------------------------------------------------------------------------- Sekmeler

function AreasTab({ draft, setDraft, canWrite }: TabProps) {
  const t = useT();
  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/v1/admin/categories'),
  });
  return (
    <ListTab<Area>
      title={t(`${N}.tabs.areas`)}
      lead={t(`${N}.areasLead`)}
      help="needs_finder.areas"
      items={draft.areas}
      setItems={(areas) => setDraft({ ...draft, areas })}
      canWrite={canWrite}
      rowTitle={(a) => textOf(a.label, 'tr')}
      rowMeta={(a) => (
        <>
          {a.species.map((s) => t(`admin.products.speciesNames.${s}`)).join(', ')}
          {'; '}
          {t(`${N}.rulesCount`, { count: draft.rules.filter((r) => r.when.areas?.includes(a.id)).length })}
        </>
      )}
      newItem={() => ({ id: '', label: { tr: '' }, species: ['cat', 'dog'], categoryIds: [] })}
      addLabel={t(`${N}.addArea`)}
      emptyLabel={t(`${N}.noAreas`)}
      dialogTitle={t(`${N}.areaDialog`)}
      editor={(a, set) => (
        <div className="space-y-4">
          <LocalizedField
            label={t(`${N}.areaLabel`)}
            help="needs_finder.area_label"
            value={a.label}
            onChange={(label) =>
              set({
                ...a,
                label: label ?? { tr: '' },
                id:
                  a.id ||
                  idFrom(
                    label?.tr ?? '',
                    draft.areas.map((x) => x.id),
                    'alan',
                  ),
              })
            }
          />
          <LocalizedField
            label={t(`${N}.areaDescription`)}
            help="needs_finder.area_description"
            value={a.description}
            optional
            multiline
            onChange={(description) => set({ ...a, description })}
          />
          <CheckGroup
            legend={t(`${N}.areaSpecies`)}
            help="needs_finder.area_species"
            values={SPECIES}
            selected={a.species}
            labelOf={(s) => t(`admin.products.speciesNames.${s}`)}
            onChange={(species) => set({ ...a, species: (species ?? []) as Species[] })}
          />
          <CheckGroup
            legend={t(`${N}.areaCategories`)}
            help="needs_finder.area_categories"
            values={(cats.data ?? []).map((c) => c.id)}
            selected={a.categoryIds}
            labelOf={(id) => cats.data?.find((c) => c.id === id)?.name ?? id}
            onChange={(ids) => set({ ...a, categoryIds: ids ?? [] })}
          />
          <IdField value={a.id} onChange={(id) => set({ ...a, id })} />
        </div>
      )}
    />
  );
}

function IdField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = useT();
  return (
    <Field label={t(`${N}.id`)} description={t(`${N}.idLead`)} help={<HelpTip k="needs_finder.id" />}>
      {(p) => (
        <Input
          {...p}
          value={value}
          maxLength={40}
          onChange={(e) => onChange(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
        />
      )}
    </Field>
  );
}

function QuestionsTab({ draft, setDraft, canWrite }: TabProps) {
  const t = useT();
  const cond = useConditionText(draft);
  return (
    <ListTab<Question>
      title={t(`${N}.tabs.questions`)}
      lead={t(`${N}.questionsLead`)}
      help="needs_finder.questions"
      items={draft.questions}
      setItems={(questions) => setDraft({ ...draft, questions })}
      canWrite={canWrite}
      rowTitle={(q) => textOf(q.text, 'tr')}
      rowMeta={(q) => (
        <>
          {q.areaId ? textOf(draft.areas.find((a) => a.id === q.areaId)?.label, 'tr') : t(`${N}.general`)}
          {'; '}
          {t(`${N}.optionsCount`, { count: q.options.length })}
          {q.options.some((o) => o.emergency) && (
            <>
              {'; '}
              <span className="text-rose">{t(`${N}.hasEmergency`)}</span>
            </>
          )}
          {q.when && (
            <>
              {'; '}
              {t(`${N}.askedWhen`, { condition: cond(q.when) })}
            </>
          )}
        </>
      )}
      newItem={() => ({
        id: '',
        areaId: null,
        text: { tr: '' },
        multiple: false,
        options: [
          { id: 'evet', label: { tr: t(`${N}.defaultYes`) }, emergency: false, exclusive: false },
          { id: 'hayir', label: { tr: t(`${N}.defaultNo`) }, emergency: false, exclusive: false },
        ],
      })}
      addLabel={t(`${N}.addQuestion`)}
      emptyLabel={t(`${N}.noQuestions`)}
      dialogTitle={t(`${N}.questionDialog`)}
      editor={(q, set) => (
        <div className="space-y-4">
          <LocalizedField
            label={t(`${N}.questionText`)}
            help="needs_finder.question_text"
            value={q.text}
            onChange={(text) =>
              set({
                ...q,
                text: text ?? { tr: '' },
                id:
                  q.id ||
                  idFrom(
                    text?.tr ?? '',
                    draft.questions.map((x) => x.id),
                    'soru',
                  ),
              })
            }
          />
          <LocalizedField
            label={t(`${N}.questionHelp`)}
            help="needs_finder.question_help"
            value={q.help}
            optional
            onChange={(help) => set({ ...q, help })}
          />
          <Field label={t(`${N}.questionArea`)} help={<HelpTip k="needs_finder.question_area" />}>
            {(p) => (
              <Select
                {...p}
                value={q.areaId ?? ''}
                onChange={(e) => set({ ...q, areaId: e.target.value || null })}
              >
                <option value="">{t(`${N}.general`)}</option>
                {draft.areas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {textOf(a.label, 'tr')}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="accent-[var(--zt-pine)]"
              checked={q.multiple}
              onChange={(e) => set({ ...q, multiple: e.target.checked })}
            />
            {t(`${N}.questionMultiple`)} <HelpTip k="needs_finder.question_multiple" />
          </label>
          <OptionsEditor q={q} set={set} />
          <details>
            <summary className="cursor-pointer text-sm font-medium">{t(`${N}.askWhen`)}</summary>
            <div className="mt-2">
              <ConditionEditor
                value={q.when}
                draft={draft}
                excludeQuestion={q.id}
                onChange={(when) => set({ ...q, when: Object.keys(when).length ? when : undefined })}
              />
            </div>
          </details>
          <IdField value={q.id} onChange={(id) => set({ ...q, id })} />
        </div>
      )}
    />
  );
}

function OptionsEditor({ q, set }: { q: Question; set: (q: Question) => void }) {
  const t = useT();
  const setOpt = (i: number, o: Option) => set({ ...q, options: q.options.map((x, j) => (j === i ? o : x)) });
  return (
    <fieldset className="space-y-3">
      <legend className="flex items-center gap-1 text-sm font-medium">
        {t(`${N}.options`)} <HelpTip k="needs_finder.options" />
      </legend>
      {q.options.map((o, i) => (
        <div key={i} className="space-y-2 rounded-md border border-line p-3">
          <LocalizedField
            label={t(`${N}.optionLabel`, { n: i + 1 })}
            help="needs_finder.option_label"
            value={o.label}
            onChange={(label) =>
              setOpt(i, {
                ...o,
                label: label ?? { tr: '' },
                id:
                  o.id ||
                  idFrom(
                    label?.tr ?? '',
                    q.options.map((x) => x.id),
                    'secenek',
                  ),
              })
            }
          />
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                className="accent-[var(--zt-rose)]"
                checked={o.emergency}
                onChange={(e) =>
                  setOpt(i, {
                    ...o,
                    emergency: e.target.checked,
                    exclusive: e.target.checked ? false : o.exclusive,
                  })
                }
              />
              {t(`${N}.optionEmergency`)} <HelpTip k="needs_finder.option_emergency" />
            </label>
            {q.multiple && (
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  className="accent-[var(--zt-pine)]"
                  checked={o.exclusive}
                  disabled={o.emergency}
                  onChange={(e) => setOpt(i, { ...o, exclusive: e.target.checked })}
                />
                {t(`${N}.optionExclusive`)} <HelpTip k="needs_finder.option_exclusive" />
              </label>
            )}
            <Button
              variant="ghost"
              size="sm"
              disabled={q.options.length <= 2}
              aria-label={t(`${N}.delete`, { name: textOf(o.label, 'tr') || `#${i + 1}` })}
              onClick={() => set({ ...q, options: q.options.filter((_, j) => j !== i) })}
            >
              <Trash2 size={16} aria-hidden />
            </Button>
          </div>
        </div>
      ))}
      <Button
        size="sm"
        disabled={q.options.length >= 12}
        onClick={() =>
          set({
            ...q,
            options: [...q.options, { id: '', label: { tr: '' }, emergency: false, exclusive: false }],
          })
        }
      >
        <Plus size={16} aria-hidden /> {t(`${N}.addOption`)}
      </Button>
    </fieldset>
  );
}

function RulesTab({ draft, setDraft, canWrite, titles }: TabProps & { titles: Map<string, string> }) {
  const t = useT();
  const cond = useConditionText(draft);
  return (
    <ListTab<Rule>
      title={t(`${N}.tabs.rules`)}
      lead={t(`${N}.rulesLead`)}
      help="needs_finder.rules"
      items={draft.rules}
      setItems={(rules) => setDraft({ ...draft, rules })}
      canWrite={canWrite}
      rowTitle={(r) => titles.get(r.productId) ?? t(`${N}.unsavedProduct`)}
      rowMeta={(r) => (
        <>
          {cond(r.when)}
          {'; '}
          {t(`${N}.priorityValue`, { n: r.priority })}
          {r.alternatives.length > 0 && (
            <>
              {'; '}
              {t(`${N}.alternativesCount`, { count: r.alternatives.length })}
            </>
          )}
          <span className="block">{textOf(r.reason, 'tr')}</span>
        </>
      )}
      newItem={() => ({
        id: idFrom(
          'kural',
          draft.rules.map((x) => x.id),
          'kural',
        ),
        when: {},
        productId: '',
        reason: { tr: '' },
        priority: 50,
        alternatives: [],
      })}
      addLabel={t(`${N}.addRule`)}
      emptyLabel={t(`${N}.noRules`)}
      dialogTitle={t(`${N}.ruleDialog`)}
      editor={(r, set) => (
        <div className="space-y-4">
          <ProductMultiPicker
            label={t(`${N}.ruleProduct`)}
            help={<HelpTip k="needs_finder.rule_product" />}
            value={r.productId ? [r.productId] : []}
            onChange={(ids) => set({ ...r, productId: ids[ids.length - 1] ?? '' })}
            max={1}
          />
          <LocalizedField
            label={t(`${N}.ruleReason`)}
            help="needs_finder.rule_reason"
            value={r.reason}
            multiline
            onChange={(reason) => set({ ...r, reason: reason ?? { tr: '' } })}
          />
          <ConditionEditor value={r.when} draft={draft} onChange={(when) => set({ ...r, when })} />
          <Field
            label={t(`${N}.rulePriority`)}
            description={t(`${N}.rulePriorityLead`)}
            help={<HelpTip k="needs_finder.rule_priority" />}
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                min={0}
                max={100}
                value={r.priority}
                onChange={(e) =>
                  set({ ...r, priority: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })
                }
              />
            )}
          </Field>
          <ProductMultiPicker
            label={t(`${N}.ruleAlternatives`)}
            help={<HelpTip k="needs_finder.rule_alternatives" />}
            value={r.alternatives}
            onChange={(alternatives) =>
              set({ ...r, alternatives: alternatives.filter((a) => a !== r.productId) })
            }
            max={5}
          />
          <IdField value={r.id} onChange={(id) => set({ ...r, id })} />
        </div>
      )}
    />
  );
}

function ExclusionsTab({ draft, setDraft, canWrite, titles }: TabProps & { titles: Map<string, string> }) {
  const t = useT();
  const cond = useConditionText(draft);
  return (
    <ListTab<Exclusion>
      title={t(`${N}.tabs.exclusions`)}
      lead={t(`${N}.exclusionsLead`)}
      help="needs_finder.exclusions"
      items={draft.exclusions}
      setItems={(exclusions) => setDraft({ ...draft, exclusions })}
      canWrite={canWrite}
      rowTitle={(x) => cond(x.when)}
      rowMeta={(x) => (
        <>
          {x.productIds.map((p) => titles.get(p) ?? t(`${N}.unsavedProduct`)).join(', ')}
          {x.note && <span className="block">{textOf(x.note, 'tr')}</span>}
        </>
      )}
      newItem={() => ({
        id: idFrom(
          'haric',
          draft.exclusions.map((x) => x.id),
          'haric',
        ),
        when: {},
        productIds: [],
      })}
      addLabel={t(`${N}.addExclusion`)}
      emptyLabel={t(`${N}.noExclusions`)}
      dialogTitle={t(`${N}.exclusionDialog`)}
      editor={(x, set) => (
        <div className="space-y-4">
          <ConditionEditor value={x.when} draft={draft} onChange={(when) => set({ ...x, when })} />
          <ProductMultiPicker
            label={t(`${N}.exclusionProducts`)}
            help={<HelpTip k="needs_finder.exclusion_products" />}
            value={x.productIds}
            onChange={(productIds) => set({ ...x, productIds })}
            max={100}
          />
          <LocalizedField
            label={t(`${N}.exclusionNote`)}
            help="needs_finder.exclusion_note"
            value={x.note}
            optional
            onChange={(note) => set({ ...x, note })}
          />
          <IdField value={x.id} onChange={(id) => set({ ...x, id })} />
        </div>
      )}
    />
  );
}

function TextsTab({ draft, setDraft, canWrite, storeId }: TabProps & { storeId: string }) {
  const t = useT();
  const { can } = usePermissions();
  const promos = useQuery({
    queryKey: ['promotions'],
    queryFn: () => api.get<Promotion[]>('/v1/admin/promotions'),
    enabled: can('pricing.price.read'),
  });
  const own = (promos.data ?? []).filter((p) => p.storeId === storeId);
  return (
    <Section
      title={t(`${N}.tabs.texts`)}
      description={t(`${N}.textsLead`)}
      action={<HelpTip k="needs_finder.texts" />}
    >
      <div className="max-w-2xl space-y-5">
        <LocalizedField
          label={t(`${N}.intro`)}
          help="needs_finder.intro"
          value={draft.texts.intro}
          optional
          multiline
          disabled={!canWrite}
          onChange={(intro) => setDraft({ ...draft, texts: { ...draft.texts, intro } })}
        />
        <LocalizedField
          label={t(`${N}.emergencyText`)}
          help="needs_finder.emergency_text"
          value={draft.texts.emergency}
          optional
          multiline
          disabled={!canWrite}
          onChange={(emergency) => setDraft({ ...draft, texts: { ...draft.texts, emergency } })}
        />
        <Field
          label={t(`${N}.bundle`)}
          description={t(`${N}.bundleLead`)}
          help={<HelpTip k="needs_finder.bundle" />}
        >
          {(p) =>
            can('pricing.price.read') ? (
              <Select
                {...p}
                value={draft.bundlePromotionId ?? ''}
                disabled={!canWrite}
                onChange={(e) => setDraft({ ...draft, bundlePromotionId: e.target.value || null })}
              >
                <option value="">{t(`${N}.bundleOff`)}</option>
                {own.map((pr) => (
                  <option key={pr.id} value={pr.id}>
                    {pr.name} ({t(`admin.promotions.phases.${pr.phase}`)})
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                {...p}
                value={draft.bundlePromotionId ? t(`${N}.bundleLinked`) : t(`${N}.bundleOff`)}
                readOnly
              />
            )
          }
        </Field>
      </div>
    </Section>
  );
}

/** Önizleme: vitrindeki kural motoruyla aynı hesap (stok dikkate alınmaz; kaydedilmiş ürün bilgisi). */
function PreviewTab({
  draft,
  set,
  titles,
}: {
  draft: Def;
  set: NeedsFinderSet;
  titles: Map<string, string>;
}) {
  const t = useT();
  const [s, setS] = useState<NfState>({ ...emptyState(), species: 'cat', age: 'adult' });
  const catalog = useMemo(
    () =>
      new Map<string, NfProduct>(
        set.products.map((p) => [
          p.id,
          { id: p.id, species: p.species, inStock: true, available: p.visible && p.status === 'active' },
        ]),
      ),
    [set.products],
  );
  const b = basicsFor(s.species, s.neutered);
  const areas = areasFor(draft, s.species);
  const questions = visibleQuestions(draft, s);
  const result = recommend(draft, s, catalog);
  const name = useId();
  const sel = <K extends keyof NfState>(k: K, v: NfState[K]) => setS({ ...s, [k]: v });
  const select = (
    label: string,
    k: 'age' | 'weight' | 'neutered' | 'reproductive',
    values: readonly string[],
    labels: string,
  ) => (
    <Field label={label}>
      {(p) => (
        <Select
          {...p}
          value={(s[k] as string | null) ?? ''}
          onChange={(e) => sel(k, (e.target.value || null) as never)}
        >
          <option value="">—</option>
          {values.map((v) => (
            <option key={v} value={v}>
              {t(`storefront.needsFinder.${labels}.${v}`)}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
  return (
    <Section
      title={t(`${N}.tabs.preview`)}
      description={t(`${N}.previewLead`)}
      action={<HelpTip k="needs_finder.preview" />}
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <Field label={t(`${N}.cond.species`)}>
            {(p) => (
              <Select
                {...p}
                value={s.species ?? ''}
                onChange={(e) =>
                  setS({ ...emptyState(), species: (e.target.value || null) as Species | null, age: s.age })
                }
              >
                {SPECIES.map((sp) => (
                  <option key={sp} value={sp}>
                    {t(`admin.products.speciesNames.${sp}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            {select(t('storefront.needsFinder.age'), 'age', AGE_BANDS, 'ages')}
            {b.weight && select(t('storefront.needsFinder.weight'), 'weight', WEIGHT_BANDS, 'weights')}
            {b.neutered &&
              select(t('storefront.needsFinder.neutered'), 'neutered', NEUTERED, 'neuteredOptions')}
            {b.reproductive &&
              select(
                t('storefront.needsFinder.reproductive'),
                'reproductive',
                REPRODUCTIVE,
                'reproductiveOptions',
              )}
          </div>
          <CheckGroup
            legend={t(`${N}.cond.areas`)}
            values={areas.map((a) => a.id)}
            selected={s.areas}
            labelOf={(id) => textOf(areas.find((a) => a.id === id)?.label, 'tr')}
            onChange={(ids) => setS({ ...s, areas: ids ?? [] })}
          />
          {questions.map((q) => (
            <fieldset key={q.id} className="space-y-1">
              <legend className="text-sm font-medium">{textOf(q.text, 'tr')}</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {q.options.map((o) => (
                  <label key={o.id} className="inline-flex items-center gap-1.5 text-sm">
                    <input
                      type={q.multiple ? 'checkbox' : 'radio'}
                      name={`${name}-${q.id}`}
                      checked={(s.answers[q.id] ?? []).includes(o.id)}
                      onChange={() => {
                        const prev = s.answers[q.id] ?? [];
                        const next = !q.multiple
                          ? [o.id]
                          : prev.includes(o.id)
                            ? prev.filter((x) => x !== o.id)
                            : [...prev, o.id];
                        setS({ ...s, answers: { ...s.answers, [q.id]: next } });
                      }}
                    />
                    {textOf(o.label, 'tr')}
                    {o.emergency && <span className="text-rose">({t(`${N}.emergencyShort`)})</span>}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
        <div className="space-y-3" aria-live="polite">
          <h3 className="font-medium">{t(`${N}.previewResult`)}</h3>
          {result.emergency ? (
            <p className="rounded-md border border-rose/30 bg-rose-tint p-3 text-sm">
              {t('storefront.needsFinder.emergencyTitle')}
            </p>
          ) : result.items.length ? (
            <ol className="space-y-2 text-sm">
              {result.items.map((i) => (
                <li key={i.productId} className="rounded-md border border-line p-2">
                  <strong>{titles.get(i.productId) ?? i.productId}</strong>
                  <ul className="list-disc ps-5 text-ink-2">
                    {i.reasons.map((r, j) => (
                      <li key={j}>{textOf(r, 'tr')}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-ink-2">{t('storefront.needsFinder.empty')}</p>
          )}
          {result.removed.length > 0 && (
            <div className="text-sm">
              <p className="font-medium">{t(`${N}.removedTitle`)}</p>
              <ul className="list-disc ps-5 text-ink-2">
                {result.removed.map((r) => (
                  <li key={r.productId}>
                    {titles.get(r.productId) ?? r.productId}: {t(`${N}.removed.${r.cause}`)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[0.8125rem] text-ink-3">{t(`${N}.previewNote`)}</p>
        </div>
      </div>
    </Section>
  );
}

function ReportTab({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatNumber } = useI18n();
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ['needs-finder-report', storeId, days],
    queryFn: () =>
      api.get<NeedsFinderReport>(`/v1/admin/needs-finder/report?storeId=${storeId}&days=${days}`),
  });
  const r = q.data;
  const rate = r && r.totals.starts ? Math.round((r.totals.completes * 100) / r.totals.starts) : null;
  return (
    <Section
      title={t(`${N}.tabs.report`)}
      description={t(`${N}.reportLead`)}
      action={<HelpTip k="needs_finder.report" />}
    >
      <div className="space-y-4">
        <Field
          label={t(`${N}.reportDays`)}
          className="max-w-xs"
          help={<HelpTip k="needs_finder.report_days" />}
        >
          {(p) => (
            <Select {...p} value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[7, 30, 90].map((d) => (
                <option key={d} value={d}>
                  {t(`${N}.lastDays`, { n: d })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {!r ? (
          <Spinner label={t('common.loading')} />
        ) : !r.totals.starts && !r.totals.emergencies ? (
          <p className="text-sm text-ink-2">{t(`${N}.reportEmpty`)}</p>
        ) : (
          <>
            <dl className="grid gap-3 sm:grid-cols-5">
              {(
                [
                  ['starts', r.totals.starts],
                  ['completes', r.totals.completes],
                  ['emergencies', r.totals.emergencies],
                  ['addAll', r.totals.addAll],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className="rounded-md border border-line p-3">
                  <dt className="text-[0.8125rem] text-ink-2">{t(`${N}.metrics.${k}`)}</dt>
                  <dd className="text-xl font-semibold tabular-nums">{formatNumber(v)}</dd>
                </div>
              ))}
              <div className="rounded-md border border-line p-3">
                <dt className="text-[0.8125rem] text-ink-2">{t(`${N}.metrics.rate`)}</dt>
                <dd className="text-xl font-semibold tabular-nums">{rate === null ? '—' : `%${rate}`}</dd>
              </div>
            </dl>
            <table className="w-full max-w-xl text-sm" aria-label={t(`${N}.bySpecies`)}>
              <caption className="text-start font-medium">{t(`${N}.bySpecies`)}</caption>
              <thead className="text-ink-2">
                <tr>
                  <th className="py-1 text-start font-medium">{t(`${N}.colSpecies`)}</th>
                  <th className="py-1 text-end font-medium">{t(`${N}.metrics.starts`)}</th>
                  <th className="py-1 text-end font-medium">{t(`${N}.metrics.completes`)}</th>
                  <th className="py-1 text-end font-medium">{t(`${N}.metrics.emergencies`)}</th>
                </tr>
              </thead>
              <tbody>
                {r.bySpecies.map((row) => (
                  <tr key={row.species} className="border-t border-line">
                    <td className="py-1">{t(`admin.products.speciesNames.${row.species}`)}</td>
                    <td className="py-1 text-end tabular-nums">{formatNumber(row.starts)}</td>
                    <td className="py-1 text-end tabular-nums">{formatNumber(row.completes)}</td>
                    <td className="py-1 text-end tabular-nums">{formatNumber(row.emergencies)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[0.8125rem] text-ink-3">{t(`${N}.reportPrivacy`)}</p>
          </>
        )}
      </div>
    </Section>
  );
}
